/** Indica si la respuesta tiene valor capturado. */
function isAnswered(ans: any): boolean {
  if (!ans) return false;
  return ans.answer != null && String(ans.answer).trim() !== '';
}

/** Aplana las respuestas a sus items hoja. */
function leafItems(answers: any[]): any[] {
  const out: any[] = [];
  for (const ans of (answers || [])) collectLeaf(ans, out);
  return out;
}

/** Agrega los subitems o la respuesta al listado. */
function collectLeaf(ans: any, out: any[]): void {
  const subs = ans.subitems || [];
  if (subs.length) pushSubs(subs, out);
  else out.push(ans);
}

/** Agrega cada subitem al listado plano. */
function pushSubs(subs: any[], out: any[]): void {
  for (const sub of subs) out.push(sub);
}

/** Calcula el progreso unitario de una respuesta. */
function answerProgressValue(ans: any): number {
  if (!isAnswered(ans)) return 0;
  return progressForNeed(ans, evidenceNeeded(ans));
}

/** Resuelve el progreso según la evidencia requerida. */
function progressForNeed(ans: any, need: string | null): number {
  if (optionalEvidence(need)) return 1;
  return hasEvidence(ans) ? 1 : 0.5;
}

/** Indica si la evidencia es opcional o innecesaria. */
function optionalEvidence(need: string | null): boolean {
  return !need || need === 'none';
}

/** Suma el progreso unitario de los items hoja. */
function sumProgress(leaves: any[]): number {
  let sum = 0;
  for (const leaf of leaves) sum += answerProgressValue(leaf);
  return sum;
}

/** Convierte una suma en porcentaje redondeado. */
function percentOf(sum: number, total: number): number {
  return Math.round((sum / total) * 100);
}

/** Calcula el progreso de la categoría en porcentaje. */
export function categoryProgress(answers: any[]): number {
  const leaves = leafItems(answers);
  if (!leaves.length) return 0;
  return percentOf(sumProgress(leaves), leaves.length);
}

/** Calcula el progreso de una respuesta en porcentaje. */
export function answerProgress(ans: any): number {
  return Math.round(answerProgressValue(ans) * 100);
}

/** Indica si la respuesta tiene archivo o comentarios. */
function hasEvidence(ans: any): boolean {
  return Boolean(ans?.fileId || (ans?.comments && String(ans.comments).trim() !== ''));
}

/** Resuelve la evidencia requerida por la respuesta. */
function evidenceNeeded(ans: any): string | null {
  return needForQuestion(ans?.question, ans);
}

/** Resuelve la evidencia según la pregunta configurada. */
function needForQuestion(q: any, ans: any): string | null {
  if (missingQuestion(q)) return null;
  if (isFileQuestion(q)) return null;
  return needForAnswer(q, ans);
}

/** Indica si la pregunta no exige evidencia. */
function missingQuestion(q: any): boolean {
  return !q || !q.evidenceRequired;
}

/** Indica si la pregunta espera un archivo directo. */
function isFileQuestion(q: any): boolean {
  return q.questionType?.dataType === 'file';
}

/** Resuelve la evidencia booleana o de archivo. */
function needForAnswer(q: any, ans: any): string | null {
  if (hasBooleanEvidence(q)) return booleanEvidence(q, ans);
  return 'file';
}

/** Indica si define evidencia por valor booleano. */
function hasBooleanEvidence(q: any): boolean {
  return Boolean(q.evidenceTrue || q.evidenceFalse);
}

/** Resuelve la evidencia según el valor sí o no. */
function booleanEvidence(q: any, ans: any): string | null {
  if (ans.answer === 'true') return q.evidenceTrue;
  if (ans.answer === 'false') return q.evidenceFalse;
  return null;
}

/** Indica si la respuesta está completa con evidencia. */
function isComplete(ans: any): boolean {
  if (!isAnswered(ans)) return false;
  return completeForNeed(ans, evidenceNeeded(ans));
}

/** Verifica la evidencia cuando la respuesta la requiere. */
function completeForNeed(ans: any, need: string | null): boolean {
  if (optionalEvidence(need)) return true;
  return hasEvidence(ans);
}

/** Calcula el progreso de los subitems en porcentaje. */
export function subProgress(parentAnswer: any): number {
  return progressOfSubs(parentAnswer, subsOf(parentAnswer));
}

/** Obtiene los subitems o lista vacía. */
function subsOf(parentAnswer: any): any[] {
  return parentAnswer?.subitems || [];
}

/** Calcula el progreso de la lista de subitems. */
function progressOfSubs(parentAnswer: any, subs: any[]): number {
  if (!subs.length) return answerProgress(parentAnswer);
  return percentOf(sumProgress(subs), subs.length);
}

/** Obtiene el regex del tipo de pregunta. */
export function regexOf(answer: any): string | null {
  return answer?.question?.questionType?.regex || null;
}

/** Valida el valor contra el regex indicado. */
export function isValidAnswer(answer: any, regex: string | null): boolean {
  if (!regex) return true;
  return matchesRegex(normalizeBlank(answer), regex);
}

/** Normaliza el valor en blanco a cadena vacía. */
function normalizeBlank(answer: any): string {
  const value = answer ?? '';
  if (String(value).trim() === '') return '';
  return String(value);
}

/** Aplica el regex salvo valor vacío permitido. */
function matchesRegex(value: string, regex: string): boolean {
  if (value === '') return true;
  return testRegex(value, regex);
}

/** Prueba el regex protegiendo patrones inválidos. */
function testRegex(value: string, regex: string): boolean {
  try {
    return new RegExp(regex).test(value);
  } catch {
    return true;
  }
}

/** Entrega el regex por defecto para números. */
export function defaultNumberRegex(): string {
  return String.raw`^-?\d+(\.\d+)?$`;
}
